import React, { useState, useEffect } from 'react';
import {
   Container,
   Box,
   Typography,
   Card,
   CardContent,
   Button,
   Avatar,
   Chip,
   CircularProgress,
   Alert,
   useMediaQuery,
   useTheme,
   Paper,
} from '@mui/material';
import {
   VideoCall as VideoCallIcon,
   Schedule as ScheduleIcon,
   Person as PersonIcon,
   AccessTime as AccessTimeIcon,
   CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format, isPast, isFuture, addMinutes } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import { checkAuthAndRedirect } from '../../services/authService';

interface ScheduledCall {
   id: string;
   title: string;
   description?: string;
   startDate: Date;
   endDate: Date;
   organizerId: string;
   organizerName: string;
   organizerEmail: string;
   participants: Array<{
      userId: string;
      email: string;
      displayName: string;
      photoURL?: string;
      status: 'pending' | 'accepted' | 'declined';
      role: 'organizer' | 'participant';
   }>;
   status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
   virtualRoomSid?: string;
   virtualRoomName?: string;
   virtualRoomUrl?: string;
   joinUrl?: string;
   createdAt: Date;
}

const ScheduledCallPublic: React.FC = () => {
   const { callId } = useParams<{ callId: string }>();
   const { currentUser } = useUser();
   const { signIn } = useAuth();
   const navigate = useNavigate();

   // Get responsive breakpoints
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [call, setCall] = useState<ScheduledCall | null>(null);
   const [processing, setProcessing] = useState(false);

   // Check authentication and redirect if needed
   useEffect(() => {
      const performAuthCheck = async () => {
         if (!callId) {
            setError('No call ID provided');
            setLoading(false);
            return;
         }

         try {
            const authResult = await checkAuthAndRedirect(
               callId,
               currentUser,
               undefined,
               navigate
            );

            if (!authResult.shouldProceed && authResult.redirectPath) {
               console.log(
                  'Scheduled call - User authentication check failed, redirecting to:',
                  authResult.redirectPath
               );
               return;
            }
         } catch (error) {
            console.error('Scheduled call auth check failed:', error);
            navigate('/login');
         }
      };

      performAuthCheck();
   }, [callId, currentUser, navigate]);

   // Fetch call details
   useEffect(() => {
      const fetchCallDetails = async () => {
         if (!callId) return;

         try {
            setLoading(true);
            setError(null);

            const callDoc = await getDoc(doc(db, 'scheduledCalls', callId));

            if (!callDoc.exists()) {
               setError('Call not found or has been cancelled');
               setLoading(false);
               return;
            }

            const callData = callDoc.data();
            const callStartDate = callData.startDate?.toDate() || new Date();
            const callEndDate = callData.endDate?.toDate() || new Date();

            setCall({
               id: callDoc.id,
               title: callData.title,
               description: callData.description,
               startDate: callStartDate,
               endDate: callEndDate,
               organizerId: callData.organizerId,
               organizerName: callData.organizerName,
               organizerEmail: callData.organizerEmail,
               participants: callData.participants || [],
               status: callData.status,
               virtualRoomSid: callData.virtualRoomSid,
               virtualRoomName: callData.virtualRoomName,
               virtualRoomUrl: callData.virtualRoomUrl,
               joinUrl: callData.joinUrl,
               createdAt: callData.createdAt?.toDate() || new Date(),
            });
         } catch (err) {
            console.error('Error fetching call details:', err);
            setError('Failed to load call details');
         } finally {
            setLoading(false);
         }
      };

      fetchCallDetails();
   }, [callId]);

   const handleJoinCall = async () => {
      if (!currentUser) {
         // User not authenticated, redirect to login with return URL
         const currentUrl = window.location.pathname;
         navigate(`/login?redirect=${encodeURIComponent(currentUrl)}`);
         return;
      }

      if (!call) return;

      setProcessing(true);

      try {
         // Check if user is a participant
         const userParticipant = call.participants.find(
            (p) => p.userId === currentUser.uid
         );

         if (!userParticipant) {
            setError('You are not authorized to join this call');
            setProcessing(false);
            return;
         }

         // Check if call is in the future
         if (isFuture(call.startDate)) {
            setError('This call has not started yet. Please wait until the scheduled time.');
            setProcessing(false);
            return;
         }

         // Check if call is in the past
         if (isPast(call.endDate)) {
            setError('This call has already ended.');
            setProcessing(false);
            return;
         }

         // Navigate to the scheduled call video page
         navigate(`/scheduled-call-room/${callId}`);
      } catch (err) {
         console.error('Error joining call:', err);
         setError('Failed to join call. Please try again.');
      } finally {
         setProcessing(false);
      }
   };

   const handleSignUp = () => {
      // Redirect to signup with return URL and invited flag
      const currentUrl = window.location.pathname;
      navigate(`/signup?redirect=${encodeURIComponent(currentUrl)}&invited=true`);
   };

   if (loading) {
      return (
         <Container
            maxWidth="sm"
            sx={{
               mt: isMobile ? 2 : 4,
               px: isMobile ? 1 : 2,
               minHeight: '100vh',
               bgcolor: isMobile ? 'grey.50' : 'background.default',
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: isMobile ? '40vh' : '50vh',
               }}
            >
               <CircularProgress size={isMobile ? 40 : 48} />
            </Box>
         </Container>
      );
   }

   if (error || !call) {
      return (
         <Container
            maxWidth="sm"
            sx={{
               mt: isMobile ? 2 : 4,
               px: isMobile ? 1 : 2,
               minHeight: '100vh',
               bgcolor: isMobile ? 'grey.50' : 'background.default',
            }}
         >
            <Paper
               sx={{
                  p: isMobile ? 1.5 : 2.5,
                  textAlign: 'center',
                  borderRadius: isMobile ? 3 : 2,
                  boxShadow: isMobile ? 2 : 3,
               }}
            >
               <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  gutterBottom
                  sx={{
                     fontSize: isMobile ? '1.2rem' : '1.5rem',
                     fontWeight: 600,
                     color: 'error.main',
                  }}
               >
                  {error || 'Call Not Found'}
               </Typography>
               <Typography
                  variant="body1"
                  color="text.secondary"
                  paragraph
                  sx={{
                     fontSize: isMobile ? '0.9rem' : '1rem',
                     mb: isMobile ? 1.5 : 2,
                  }}
               >
                  {error || 'The call you are looking for does not exist or has been cancelled.'}
               </Typography>
               <Button
                  variant="contained"
                  onClick={() => navigate('/')}
                  size={isMobile ? 'large' : 'medium'}
                  sx={{
                     mt: isMobile ? 1 : 1.5,
                     py: isMobile ? 1.5 : 1,
                     px: isMobile ? 3 : 2,
                     fontSize: isMobile ? '0.9rem' : '0.875rem',
                     borderRadius: isMobile ? 2 : 1,
                  }}
               >
                  Go Home
               </Button>
            </Paper>
         </Container>
      );
   }

   const isCallActive = !isPast(call.startDate) && !isPast(call.endDate);
   const canJoin = isCallActive && call.status === 'scheduled';

   return (
      <Container
         maxWidth="md"
         sx={{
            mt: isMobile ? 2 : 4,
            px: isMobile ? 1 : 2,
            minHeight: '100vh',
            bgcolor: isMobile ? 'grey.50' : 'background.default',
         }}
      >
         <Card
            sx={{
               borderRadius: isMobile ? 3 : 2,
               boxShadow: isMobile ? 2 : 3,
               overflow: 'hidden',
            }}
         >
            {/* Header */}
            <Box
               sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: 'white',
                  p: isMobile ? 2 : 3,
                  textAlign: 'center',
               }}
            >
               <VideoCallIcon
                  sx={{
                     fontSize: isMobile ? 40 : 48,
                     mb: 1,
                  }}
               />
               <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  component="h1"
                  sx={{
                     fontWeight: 600,
                     mb: 1,
                  }}
               >
                  {call.title}
               </Typography>
               <Typography
                  variant="body2"
                  sx={{
                     opacity: 0.9,
                     fontSize: isMobile ? '0.8rem' : '0.875rem',
                  }}
               >
                  Virtual Call Invitation
               </Typography>
            </Box>

            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
               {/* Call Details */}
               <Box sx={{ mb: 3 }}>
                  <Typography
                     variant="h6"
                     gutterBottom
                     sx={{
                        fontSize: isMobile ? '1.1rem' : '1.25rem',
                        fontWeight: 600,
                        mb: 2,
                     }}
                  >
                     Call Details
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon color="primary" />
                        <Typography variant="body2" color="text.secondary">
                           {format(call.startDate, 'EEEE, MMMM do, yyyy')}
                        </Typography>
                     </Box>

                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon color="primary" />
                        <Typography variant="body2" color="text.secondary">
                           {format(call.startDate, 'h:mm a')} - {format(call.endDate, 'h:mm a')}
                        </Typography>
                     </Box>

                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" />
                        <Typography variant="body2" color="text.secondary">
                           Organized by {call.organizerName}
                        </Typography>
                     </Box>
                  </Box>

                  {call.description && (
                     <Box sx={{ mt: 2 }}>
                        <Typography
                           variant="body2"
                           color="text.secondary"
                           sx={{
                              fontStyle: 'italic',
                              p: 2,
                              bgcolor: 'grey.50',
                              borderRadius: 1,
                           }}
                        >
                           "{call.description}"
                        </Typography>
                     </Box>
                  )}
               </Box>

               {/* Status */}
               <Box sx={{ mb: 3 }}>
                  <Chip
                     icon={<ScheduleIcon />}
                     label={
                        isPast(call.endDate)
                           ? 'Call Ended'
                           : isPast(call.startDate)
                           ? 'Call in Progress'
                           : 'Scheduled'
                     }
                     color={
                        isPast(call.endDate)
                           ? 'default'
                           : isPast(call.startDate)
                           ? 'success'
                           : 'primary'
                     }
                     sx={{ mb: 2 }}
                  />
               </Box>

               {/* Action Buttons */}
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {currentUser ? (
                     // User is authenticated
                     <Button
                        variant="contained"
                        size="large"
                        startIcon={<VideoCallIcon />}
                        onClick={handleJoinCall}
                        disabled={!canJoin || processing}
                        sx={{
                           py: isMobile ? 1.5 : 1,
                           fontSize: isMobile ? '0.9rem' : '1rem',
                           borderRadius: isMobile ? 2 : 1,
                        }}
                     >
                        {processing ? (
                           <CircularProgress size={20} color="inherit" />
                        ) : canJoin ? (
                           'Join Call'
                        ) : isPast(call.endDate) ? (
                           'Call Ended'
                        ) : isFuture(call.startDate) ? (
                           'Call Not Started Yet'
                        ) : (
                           'Join Call'
                        )}
                     </Button>
                  ) : (
                     // User is not authenticated
                     <>
                        <Button
                           variant="contained"
                           size="large"
                           startIcon={<VideoCallIcon />}
                           onClick={handleJoinCall}
                           disabled={!canJoin || processing}
                           sx={{
                              py: isMobile ? 1.5 : 1,
                              fontSize: isMobile ? '0.9rem' : '1rem',
                              borderRadius: isMobile ? 2 : 1,
                           }}
                        >
                           {processing ? (
                              <CircularProgress size={20} color="inherit" />
                           ) : canJoin ? (
                              'Join Call'
                           ) : isPast(call.endDate) ? (
                              'Call Ended'
                           ) : isFuture(call.startDate) ? (
                              'Call Not Started Yet'
                           ) : (
                              'Join Call'
                           )}
                        </Button>
                        <Button
                           variant="outlined"
                           size="large"
                           onClick={handleSignUp}
                           sx={{
                              py: isMobile ? 1.5 : 1,
                              fontSize: isMobile ? '0.9rem' : '1rem',
                              borderRadius: isMobile ? 2 : 1,
                           }}
                        >
                           Sign Up to Join
                        </Button>
                     </>
                  )}
               </Box>

               {/* Additional Info */}
               <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography
                     variant="body2"
                     color="text.secondary"
                     sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                  >
                     {currentUser
                        ? 'Click "Join Call" to enter the virtual meeting room.'
                        : 'Sign up for a free account to join this call and access all features.'}
                  </Typography>
               </Box>
            </CardContent>
         </Card>
      </Container>
   );
};

export default ScheduledCallPublic;
